/**
 * Branded waiting screen with session title, agenda preview,
 * and "Session will start soon" message.
 */

import { useState, useEffect } from 'react';
import { getNodesPublic, type PublicNode } from '../lib/api';

/* ------------------------------------------------------------------ */
/*  Type config for badge display                                     */
/* ------------------------------------------------------------------ */

const TYPE_CONFIG: Record<string, { abbr: string; color: string }> = {
  question: { abbr: 'Q', color: 'bg-blue-500' },
  poll: { abbr: 'P', color: 'bg-green-500' },
  quiz: { abbr: 'Qz', color: 'bg-purple-500' },
  nps: { abbr: 'NPS', color: 'bg-orange-500' },
  crosstab: { abbr: 'CT', color: 'bg-pink-500' },
  imageactivity: { abbr: 'IA', color: 'bg-cyan-500' },
};

/* ------------------------------------------------------------------ */
/*  Component                                                         */
/* ------------------------------------------------------------------ */

interface WaitingRoomScreenProps {
  sessionId: string;
  sessionTitle?: string;
}

export function WaitingRoomScreen({ sessionId, sessionTitle }: WaitingRoomScreenProps) {
  const [nodes, setNodes] = useState<PublicNode[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        const data = await getNodesPublic(sessionId);
        if (!cancelled) setNodes(data);
      } catch (err) {
        console.warn('Could not load public nodes:', err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [sessionId]);

  return (
    <div className="flex flex-col items-center min-h-screen bg-gradient-to-b from-primary-50 to-white px-4 py-8">
      {/* Logo / Brand */}
      <div className="w-12 h-12 rounded-xl bg-primary-500 flex items-center justify-center mb-4">
        <span className="text-white font-bold text-lg">F</span>
      </div>

      {/* Session title */}
      <h1 className="text-lg font-semibold text-gray-900 text-center mb-1">
        {sessionTitle || 'Session'}
      </h1>

      {/* Status message */}
      <div className="flex items-center gap-2 mb-6">
        <span className="relative flex h-2.5 w-2.5">
          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-primary-400 opacity-75" />
          <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-primary-500" />
        </span>
        <p className="text-sm text-primary-600 font-medium">
          Session will start soon
        </p>
      </div>

      {/* Agenda preview */}
      {loading ? (
        <div className="w-full max-w-xs">
          {[1, 2, 3].map((i) => (
            <div
              key={i}
              className="h-10 bg-gray-100 rounded-lg mb-2 animate-pulse"
            />
          ))}
        </div>
      ) : nodes.length > 0 ? (
        <div className="w-full max-w-xs">
          <h2 className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-3">
            Agenda
          </h2>
          <div className="space-y-2">
            {nodes.map((node, idx) => {
              const cfg = TYPE_CONFIG[node.type] || {
                abbr: '?',
                color: 'bg-gray-400',
              };
              return (
                <div
                  key={idx}
                  className="flex items-center gap-3 bg-white rounded-lg border border-gray-100 px-3 py-2.5 shadow-sm"
                >
                  <span
                    className={`${cfg.color} text-white text-[10px] font-bold rounded px-1.5 py-0.5 min-w-[24px] text-center`}
                  >
                    {cfg.abbr}
                  </span>
                  <span className="text-sm text-gray-700 truncate flex-1">
                    {node.title}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      ) : (
        <p className="text-sm text-gray-400">
          The facilitator is preparing activities.
        </p>
      )}

      {/* Footer */}
      <p className="mt-auto pt-8 text-xs text-gray-300">
        Powered by Facilitator
      </p>
    </div>
  );
}
